import { useRef, useState, ChangeEventHandler } from "react";
import Blockly from "blockly";
import { useDialog } from "app/hook";
import { DialogType } from "app/types";
import { btnDelay, useScale } from "common/utils";
import { IconPhotoPlus, IconQrcode } from "common/assets";
import { CameraIcon } from "features/creator/property/components";
import {
  useWokspaceBlockGenerator,
  useExecutableBlockListGenerator,
} from "./useBlockGenerator";
import { Camera } from "./Camera";
import { ConnectionInfo } from "./ConnectionInfo";
import { PeerConnection } from "./PeerConnection";
import { ConnectionMessageType, ExecuteableBlock } from "./type";

export const Control = ({
  workspace,
  setEvent,
}: {
  workspace: Blockly.WorkspaceSvg;
  setEvent: (code: string) => void;
}) => {
  const TOP_OFFSET = 120 + 32;
  const COMMAND_PANEL_HEIGHT = 82;
  const { scale } = useScale();
  const handleDialog = useDialog();
  const ref = useRef<HTMLInputElement>();
  const [info, setInfo] = useState(false);
  const [local, setLocal] = useState(false);
  const [peerId, setPeerId] = useState(null);
  const [connect, setConnect] = useState(false);
  const [connected, setConnected] = useState(false);
  const { generateWorkspaceBlock } = useWokspaceBlockGenerator(
    workspace,
    scale,
    TOP_OFFSET + COMMAND_PANEL_HEIGHT,
    setEvent
  );
  const { handleGenerateBlock } = useExecutableBlockListGenerator();

  const handleOpenConnectionInfo = () => {
    btnDelay(() => {
      setInfo(true);
      setConnect(true);
    });
  };
  const handleCloseConnectionInfo = () => {
    btnDelay(() => {
      setInfo(false);
      setConnect(false);
    });
  };

  const handleLocalCamera = () => {
    btnDelay(() => setLocal(true));
  };

  const handlePickImage: ChangeEventHandler<HTMLInputElement> = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.readAsArrayBuffer(file);
      reader.onload = (_) => {
        const arrayBuffer = reader.result;
        handleGenerateBlock(
          arrayBuffer,
          (_, blocks, isAiRobotTeacher) => {
            if (blocks.length > 0) {
              generateWorkspaceBlock(blocks);
            }
            console.log("ai robot teacher: ", isAiRobotTeacher);
          },
          (error) => {
            console.log(error);
          },
          () => {
            ref.current.value = "";
          }
        );
      };
    }
  };

  const handleMessage = (message: string) => {
    console.log(message);
    try {
      const content = JSON.parse(message) as {
        type: ConnectionMessageType;
        data: any;
      };
      switch (content.type) {
        case ConnectionMessageType.BLOCK:
          const { blocks, isAiRobotTeacher } = JSON.parse(content.data) as {
            blocks: ExecuteableBlock[][];
            isAiRobotTeacher: boolean;
          };
          handleDialog({ type: DialogType.ASSISTANT, value: isAiRobotTeacher });
          generateWorkspaceBlock(blocks);
          break;
        case ConnectionMessageType.TEST:
          console.log(content.data);
          break;
        default:
          break;
      }
    } catch (error) {
      console.log("unknown message!");
    }
  };

  const handleSocketConnect = () => {
    setInfo(false);
    setConnected(true);
  };
  const handleSocketMessage = (message: string) => handleMessage(message);
  const handleSocketDisconnect = () => {
    setPeerId(null);
    setConnect(false);
    setConnected(false);
    handleDialog({ type: DialogType.ASSISTANT, value: false });
  };

  const handleSuccess = (result: string) => {
    const { blocks, isAiRobotTeacher } = JSON.parse(result);
    generateWorkspaceBlock(blocks);
    handleDialog({ type: DialogType.ASSISTANT, value: isAiRobotTeacher });
  };

  return (
    <div className="flex-col-el">
      {workspace && (
        <>
          <PeerConnection
            callee={true}
            connect={connect}
            setPeerId={setPeerId}
            handleSocketConnect={handleSocketConnect}
            handleSocketMessage={handleSocketMessage}
            handleSocketDisconnect={handleSocketDisconnect}
          />
          <Camera
            open={local}
            remote={false}
            setOpen={setLocal}
            success={handleSuccess}
          />
        </>
      )}

      <ConnectionInfo
        open={info}
        value={peerId}
        close={handleCloseConnectionInfo}
      />

      <div
        className="flex-col-el flex-center h-full gap-20 absolute"
        style={{ transform: `scale(${scale})` }}
      >
        <div
          className={`flex-col-el w-[50px] h-[50px] rounded-full ${
            connected ? "bg-safe" : "bg-red/100"
          }`}
        />

        <div className="flex-col-el flex-center upsilon px-5 pointer-events-auto">
          <button
            disabled={connected}
            className="btn btn-blue"
            onClick={handleOpenConnectionInfo}
          >
            <div className="flex-col-el flex-center w-[36px] h-[36px]">
              <IconQrcode />
            </div>
          </button>
        </div>

        <div className="flex-col-el flex-center upsilon px-5 pointer-events-auto">
          <button className="btn btn-blue" onClick={handleLocalCamera}>
            <div className="flex-col-el flex-center">
              <CameraIcon width={36} height={36} />
            </div>
          </button>
        </div>

        <div className="flex-col-el flex-center upsilon px-5 pointer-events-auto cursor-pointer">
          <input
            ref={ref}
            type="file"
            onChange={handlePickImage}
            accept="image/*"
            className="flex-row-el absolute appearance-none w-[36px] h-[36px] opacity-0 z-50 peer cursor-pointer"
          />

          <button className="btn btn-blue cursor-pointer">
            <div className="flex-col-el flex-center w-[36px] h-[36px] cursor-pointer">
              <IconPhotoPlus />
            </div>
          </button>
        </div>
      </div>
    </div>
  );
};
